import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useHighlightRow } from "../hooks/useHighlightRow";
import Layout from "../components/Layout";
import apiClient from "../services/api";
import { useHotel } from "../contexts/HotelContext";
import EditOperatorModal from "../components/grants/operators/EditOperatorModal";
import { EmptyState } from "../components/common/EmptyState";
import { operatorTypeLabels } from "../constants/grants-labels";

interface Operator {
  id: string;
  name: string;
  shortName: string | null;
  type: string;
  region: string | null;
  district: string | null;
  website: string | null;
  email: string | null;
  phone: string | null;
  isActive: boolean;
  callsCount: number;
  sourcesCount: number;
  createdAt: string;
  updatedAt: string;
}

interface OperatorsResponse {
  operators: Operator[];
  total: number;
}

/**
 * Page for managing grant operators (PUPs, WUPs, etc.).
 * Shows operators with their source and call counts.
 */
const GrantsOperators: React.FC = () => {
  const { user, hasModuleEnabled } = useHotel();
  const canWrite = user?.role === "admin" || hasModuleEnabled("admin:kiedynabory:write");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingOperatorId, setEditingOperatorId] = useState<string | null>(null);
  const [regionFilter, setRegionFilter] = useState<string>("");
  const { highlightedRowRef, highlightId } = useHighlightRow();

  const handleRowClick = (operatorId: string) => {
    navigate(`/grants/operators/${operatorId}`);
  };

  const handleRowKeyDown = (e: React.KeyboardEvent, operatorId: string) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate(`/grants/operators/${operatorId}`);
    }
  };

  const { data, isLoading, error, refetch } = useQuery<OperatorsResponse>({
    queryKey: ["grants-operators", regionFilter],
    queryFn: async () => {
      const params = regionFilter ? `?region=${regionFilter}` : "";
      const { data } = await apiClient.get(`/grants/operators${params}`);
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/grants/operators/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-operators"] });
    },
  });

  const handleDelete = (operator: Operator) => {
    if (operator.callsCount > 0 || operator.sourcesCount > 0) {
      alert(
        `Nie można usunąć operatora "${operator.name}" - ma powiązane nabory lub źródła.`
      );
      return;
    }
    if (
      window.confirm(`Czy na pewno chcesz usunąć operatora "${operator.name}"?`)
    ) {
      deleteMutation.mutate(operator.id);
    }
  };

  // Extract unique regions for filter
  const regions = React.useMemo(() => {
    if (!data?.operators) return [];
    const regionSet = new Set<string>();
    data.operators.forEach((op) => {
      if (op.region) regionSet.add(op.region);
    });
    return Array.from(regionSet).sort();
  }, [data?.operators]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Operatorzy</h1>
            <p className="mt-1 text-sm text-neutral-600">
              Lista operatorów naborów (PUP, WUP, instytucje wdrażające)
            </p>
          </div>
          {canWrite && (
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
            >
              Dodaj operatora
            </button>
          )}
        </div>

        {/* Region filter */}
        {regions.length > 0 && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-neutral-600">Region:</label>
            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Wszystkie regiony</option>
              {regions.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
          {isLoading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-neutral-600">
                Ładowanie operatorów...
              </p>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-600">Błąd podczas pobierania danych</p>
              <button
                onClick={() => refetch()}
                className="mt-2 text-primary-600 hover:text-primary-800"
              >
                Spróbuj ponownie
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Nazwa
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase">
                      Typ
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Region
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                      Naborów
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                      Źródeł
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase">
                      Status
                    </th>
                    {canWrite && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                        Akcje
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {data?.operators?.map((operator) => {
                    const isHighlighted = highlightId === operator.id;
                    return (
                    <tr
                      key={operator.id}
                      ref={isHighlighted ? highlightedRowRef : null}
                      className={`hover:bg-neutral-50 transition-colors duration-500 cursor-pointer focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 focus-visible:outline-none ${
                        isHighlighted ? "bg-yellow-100 ring-2 ring-yellow-400" : ""
                      }`}
                      onClick={() => handleRowClick(operator.id)}
                      onKeyDown={(e) => handleRowKeyDown(e, operator.id)}
                      tabIndex={0}
                      role="row"
                      aria-label={`Otwórz szczegóły operatora ${operator.name}`}
                    >
                      <td className="px-4 py-4 text-left">
                        <div className="text-sm font-medium text-neutral-900">
                          {operator.name}
                        </div>
                        {operator.shortName && (
                          <div className="text-xs text-neutral-500">
                            {operator.shortName}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <span className="text-sm text-neutral-500">
                          {operatorTypeLabels[operator.type] || operator.type}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-neutral-500">
                        {operator.region || "-"}
                        {operator.district && (
                          <span className="block text-xs">
                            {operator.district}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        {operator.callsCount > 0 ? (
                          <Link
                            to={`/grants/calls?operatorId=${operator.id}`}
                            className="text-sm text-primary-600 hover:text-primary-800"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {operator.callsCount}
                          </Link>
                        ) : (
                          <span className="text-sm text-neutral-500">0</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        {operator.sourcesCount > 0 ? (
                          <Link
                            to={`/grants/sources?operatorId=${operator.id}`}
                            className="text-sm text-primary-600 hover:text-primary-800"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {operator.sourcesCount}
                          </Link>
                        ) : (
                          <span className="text-sm text-neutral-500">0</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            operator.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-neutral-200 text-neutral-800"
                          }`}
                        >
                          {operator.isActive ? "Aktywny" : "Nieaktywny"}
                        </span>
                      </td>
                      {canWrite && (
                        <td className="px-4 py-4 whitespace-nowrap text-right cursor-default">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingOperatorId(operator.id);
                            }}
                            className="text-primary-600 hover:text-primary-800 mr-3"
                            aria-label={`Edytuj operatora ${operator.name}`}
                          >
                            Edytuj
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(operator);
                            }}
                            className="text-red-600 hover:text-red-800"
                            disabled={
                              operator.callsCount > 0 ||
                              operator.sourcesCount > 0
                            }
                            aria-label={`Usuń operatora ${operator.name}`}
                          >
                            Usuń
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                  })}
                </tbody>
              </table>

              {(!data?.operators || data.operators.length === 0) && (
                <EmptyState
                  title="Brak operatorów do wyświetlenia"
                  className="p-6"
                />
              )}
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        <EditOperatorModal
          isOpen={isAddModalOpen || !!editingOperatorId}
          onClose={() => {
            setIsAddModalOpen(false);
            setEditingOperatorId(null);
          }}
          operatorId={editingOperatorId}
        />
      </div>
    </Layout>
  );
};

export default GrantsOperators;
